import { useState } from 'react';
import { StepNavigator } from '~/components/StepNavigator/StepNavigator.tsx';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import { RemoteBankingContactsStep } from '~/modules/remote-banking-contacts/components/RemoteBankingContactsStep.tsx';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useStepNavigation } from '~/components/StepNavigator/useStepNavigation.ts';
import { setRemoteBankingContactsError } from '~/store/remote-banking-contacts/slices.ts';
import {
  TManagerForm,
  TResDocumentId,
  TResponsiblePersonsForm,
} from '~/modules/remote-banking-contacts/type.ts';
import { Box, Grid } from '@mui/material';
import { styles } from '~/modules/styles.ts';
import {
  remoteBankingContactsManager,
  remoteBankingContactsManagerForm,
} from '~/store/remote-banking-contacts/thunks/managerFormThunks.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { ApplicationSignatureRemoteBankingContacts } from './ApplicationSignature/ApplicationSignatureRemoteBankingContacts';
import { previewDocumentRemoteBanking } from '~/store/remote-banking-contacts/thunks/documentPreviewRemoteBankingThunks.ts';
import {
  remoteBankContactCurrentContactDetailsInfo,
  remoteBankContactResponsiblePersonsForm,
} from '~/store/remote-banking-contacts/thunks/responsiblePersonsFormThunks.ts';
import {
  nameDocuments,
  transformManagerFormData,
  transformResponsiblePersonsFormData,
} from '~/modules/remote-banking-contacts/utils.ts';
import { ERROR_CODES } from '~/constants/errorCodes.ts';
import { HeaderBlock } from '~/components/HeaderFooter/HeaderBlock.tsx';
import { FooterBlock } from '~/components/HeaderFooter/FooterBlock.tsx';
import {
  REMOTE_BANKING_CONTACTS_STEPS,
  TITLE_TEXT,
} from '~/modules/remote-banking-contacts/constants.tsx';
import { selectToken, selectTokenData } from '~/store/users/selectors.ts';

interface IRemoteBankingContactsHomePageProps {
  isOpen: boolean;
}

export const RemoteBankingContactsHomePage = ({ isOpen }: IRemoteBankingContactsHomePageProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const token = useSelector(selectToken);
  const tokenData = useSelector(selectTokenData);
  const { applicationId } = tokenData || {};

  const [applicationDocumentId, setApplicationDocumentId] = useState<string | null>(null);
  const [current, setCurrent] = useState<number>(1);
  const [isIITOpen, setIsIITOpen] = useState<boolean>(false);
  const [isModalCongratulationsText, setIsModalCongratulationsText] = useState<string | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);

  const closeModalCongratulations = () => navigate('/');

  const handleCloseIIT = () => {
    setIsIITOpen(false);
    dispatch(setRemoteBankingContactsError(''));
    setIsSubmittingForm(false);
  };

  const { next, items } = useStepNavigation(current, setCurrent, REMOTE_BANKING_CONTACTS_STEPS);

  const handleResponsiblePersonsSubmit = async (data: TResponsiblePersonsForm) => {
    const formResponsiblePersons = transformResponsiblePersonsFormData(data);

    try {
      await dispatch(
        remoteBankContactResponsiblePersonsForm({
          applicationId: applicationId!,
          form: formResponsiblePersons,
          token,
        })
      ).unwrap();

      const res = await dispatch(
        remoteBankContactCurrentContactDetailsInfo({
          applicationId: applicationId!,
          token,
        })
      );

      setApplicationDocumentId((res.payload as TResDocumentId).documentId);
      next();
    } catch (error: any) {
      if (error?.errorCode === ERROR_CODES.INTERNAL_SERVER_ERROR) {
        dispatch(setRemoteBankingContactsError(error.message));
      } else {
        dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
      }
    }
  };

  const handleManagerSubmit = async (data: TManagerForm) => {
    const formManager = transformManagerFormData(data) as TManagerForm;

    try {
      await dispatch(
        remoteBankingContactsManagerForm({
          applicationId: applicationId!,
          form: formManager,
          token,
        })
      ).unwrap();

      const res = await dispatch(
        remoteBankingContactsManager({ applicationId: applicationId!, token })
      );

      setApplicationDocumentId((res.payload as TResDocumentId).documentId);
      next();
    } catch (err) {
      dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
    }
  };

  const handleOptionSignature = () => {
    if (isSubmittingForm) return;

    if (current === 2 || current === 4) {
      setIsSubmittingForm(true);
      setIsIITOpen(true);
      dispatch(setRemoteBankingContactsError(''));
    }
  };

  const handleSignatureSuccess = (successMessage: string) => {
    setIsModalCongratulationsText(successMessage);
    setIsIITOpen(false);
    setIsSubmittingForm(false);
  };

  const documents = nameDocuments(current, applicationDocumentId);

  const handlePreview = () => {
    dispatch(
      previewDocumentRemoteBanking({
        applicationId: applicationId!,
        idDoc: applicationDocumentId!,
        token,
      })
    )
      .unwrap()
      .catch(() => {
        dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
      });
  };

  return (
    <>
      {isOpen && (
        <Box sx={styles.mainFormPage}>
          <Grid sx={styles.mainFormPageContainer}>
            <HeaderBlock titleText={TITLE_TEXT} />
            <StepNavigator current={current} items={items} />
            <Grid sx={styles.mainFormPageContent}>
              <RemoteBankingContactsStep
                current={current}
                onOptionSignature={handleOptionSignature}
                documents={documents}
                handlePreview={handlePreview}
                onManagerSubmit={handleManagerSubmit}
                onResponsiblePersonsSubmit={handleResponsiblePersonsSubmit}
              />
            </Grid>
            <FooterBlock />
          </Grid>

          <ApplicationSignatureRemoteBankingContacts
            isModalOpen={isIITOpen}
            handleCloseModal={handleCloseIIT}
            onSignatureSuccess={handleSignatureSuccess}
            setIsSubmittingForm={setIsSubmittingForm}
            applicationDocumentId={applicationDocumentId!}
            current={current}
            onNext={next}
          />

          <SuccessModal
            isOpen={!!isModalCongratulationsText}
            text={isModalCongratulationsText}
            onClose={closeModalCongratulations}
            showButton={true}
            onClick={closeModalCongratulations}
            buttonValue="Завершити"
            dataTestId="modalSuccess"
          />
        </Box>
      )}
    </>
  );
};
